import { FrontendContentPage, Severity } from "node-library/dist/constants";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { sendGETRequest } from "../../requestHelper/sendRequest";

import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import { FrontendBatchPageTestBatchDetails } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import WorkflowManagementBatchDetailsContent from "../mainPages/batchDetailsPage/workflowManagementBatchDetailsContent";
import OrderDetailsContentSkeletonUI from "../mainPages/viewOrders/skeletonUI/orderDetailsContentSkeleton";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	batchId: string;
	user: FrontendUserInfo | undefined;

	navigateToOrderDetailsPage: (
		orderId: string,
		fromPage: FrontendContentPage,
	) => void;
	goBackToPreviousPage: (reload: boolean) => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function WorkflowManagementBatchDetailsContentWrapper(
	props: Props,
) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		batchId,
		goBackToPreviousPage,
		navigateToOrderDetailsPage,
		company,
		setUnsavedChanges,
		setCompany,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);

	const [selectedBatchDetails, setSelectedBatchDetails] =
		useState<FrontendBatchPageTestBatchDetails>();

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	const doesUserHaveAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.batches.getDetails,
			)
		: false;

	async function loadBatchDetails() {
		setLoading(true);
		try {
			// --- Step 2: Make single request ---
			const response = await sendGETRequest(
				"/sample-test-batches/get-batch-details-by-id",
				{
					batchId,
				},
			);

			const frontendBatchDetails: FrontendBatchPageTestBatchDetails =
				response?.data?.frontendBatchDetails;

			setSelectedBatchDetails(frontendBatchDetails);
			const specDirectories: Array<FrontendSampleSpecificationDirectory> =
				response?.data?.specDirectories;
			setSpecDirectories(specDirectories);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	useEffect(() => {
		if (batchId && doesUserHaveAccess) {
			loadBatchDetails();
		}
	}, [batchId]);

	function gotoInstanceDetailsPage() {
		setCurrentPageIndex(0);
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = !selectedBatchDetails ? (
				<OrderDetailsContentSkeletonUI />
			) : (
				<WorkflowManagementBatchDetailsContent
					company={company}
					setCompany={setCompany}
					batchDetails={
						selectedBatchDetails as FrontendBatchPageTestBatchDetails
					}
					setBatchDetails={setSelectedBatchDetails}
					goBackToPreviousPage={goBackToPreviousPage}
					reloadBatchDetails={loadBatchDetails}
					user={props.user as FrontendUserInfo}
					specDirectories={specDirectories}
					updateSpecification={(updatedSpec: FrontendSampleSpecification) => {
						setSelectedBatchDetails({
							...selectedBatchDetails,
							instances: selectedBatchDetails.instances.map((inst) =>
								inst.specification?.specificationId ===
								updatedSpec.specificationId
									? { ...inst, specification: updatedSpec }
									: inst,
							),
						});
					}}
				></WorkflowManagementBatchDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{doesUserHaveAccess ? (
				<>{currentComponent}</>
			) : (
				<div>{translation("YouDoNotHavePermissionToViewContent")}</div>
			)}
		</div>
	);
}
